import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BuildYourPlate = ({ menuItems, onClose, onAddToCart }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [plateSize, setPlateSize] = useState('regular');
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { id: 'base', name: 'Choose Your Base', icon: 'Bowl' },
    { id: 'protein', name: 'Select Protein', icon: 'ChefHat' },
    { id: 'sides', name: 'Add Sides', icon: 'Plus' },
    { id: 'extras', name: 'Extras & Sauces', icon: 'Droplets' }
  ];

  const plateOptions = [
    { id: 'small', name: 'Small Plate', price: 0, description: 'Perfect for light appetite', maxItems: 3 },
    { id: 'regular', name: 'Regular Plate', price: 2, description: 'Most popular choice', maxItems: 5 },
    { id: 'large', name: 'Large Plate', price: 4, description: 'For hearty appetites', maxItems: 7 }
  ];

  const categorizedItems = useMemo(() => {
    return {
      base: menuItems.filter(item => ['traditional-sides'].includes(item.category) && 
                                   ['Mediterranean Rice Pilaf'].includes(item.name)),
      protein: menuItems.filter(item => item.category === 'kebab-classics'),
      sides: menuItems.filter(item => ['mediterranean-mezze', 'fresh-salads'].includes(item.category)),
      extras: [
        { id: 'extra-1', name: 'Tahini Sauce', price: 1.50, category: 'sauce' },
        { id: 'extra-2', name: 'Garlic Sauce', price: 1.50, category: 'sauce' },
        { id: 'extra-3', name: 'Hot Sauce', price: 1.00, category: 'sauce' },
        { id: 'extra-4', name: 'Extra Pita Bread', price: 2.00, category: 'bread' },
        { id: 'extra-5', name: 'Pickled Vegetables', price: 2.50, category: 'side' },
        { id: 'extra-6', name: 'Olives & Cheese', price: 3.50, category: 'side' }
      ]
    };
  }, [menuItems]);

  const currentStepItems = categorizedItems[steps[currentStep].id] || [];
  const selectedPlateOption = plateOptions.find(option => option.id === plateSize);
  const maxItems = selectedPlateOption?.maxItems || 5;

  const totalPrice = useMemo(() => {
    const platePrice = selectedPlateOption?.price || 0;
    const itemsPrice = selectedItems.reduce((total, item) => total + item.price, 0);
    return platePrice + itemsPrice;
  }, [selectedItems, selectedPlateOption]);

  const totalNutrition = useMemo(() => {
    return selectedItems.reduce((total, item) => {
      if (item.nutritional) {
        return {
          calories: total.calories + item.nutritional.calories,
          protein: total.protein + item.nutritional.protein,
          carbs: total.carbs + item.nutritional.carbs,
          fat: total.fat + item.nutritional.fat
        };
      }
      return total;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [selectedItems]);

  const handleItemSelect = (item) => {
    if (selectedItems.find(selected => selected.id === item.id)) {
      setSelectedItems(prev => prev.filter(selected => selected.id !== item.id));
    } else if (selectedItems.length < maxItems) {
      setSelectedItems(prev => [...prev, item]);
    }
  };

  const handleAddToCart = () => {
    const plateItem = {
      id: `custom-plate-${Date.now()}`,
      name: `Custom ${selectedPlateOption.name}`,
      price: totalPrice,
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: `Your custom plate with ${selectedItems.length} selected items`,
      category: "custom",
      dietary: [],
      spiceLevel: "varies",
      mealType: "main",
      preparationTime: "15-25",
      allergens: [],
      nutritional: totalNutrition,
      customItems: selectedItems,
      plateSize: plateSize
    };
    
    onAddToCart(plateItem, 1);
    onClose();
  };

  const canProceed = () => {
    if (currentStep === 0) return selectedItems.some(item => categorizedItems.base.includes(item));
    if (currentStep === 1) return selectedItems.some(item => categorizedItems.protein.includes(item));
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-warm-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-primary-100">
          <div>
            <h2 className="font-headline text-2xl font-bold text-text-primary">
              Build Your Perfect Plate
            </h2>
            <p className="text-text-secondary">
              Create a custom combination that suits your taste
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-primary-50 transition-organic"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Steps & Plate Size */}
          <div className="w-80 bg-surface p-6 overflow-y-auto">
            {/* Plate Size Selection */}
            <div className="mb-8">
              <h3 className="font-headline text-lg font-semibold text-text-primary mb-4">
                Choose Plate Size
              </h3>
              <div className="space-y-3">
                {plateOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setPlateSize(option.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-organic ${
                      plateSize === option.id
                        ? 'border-primary bg-primary-50' :'border-primary-200 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-text-primary">
                        {option.name}
                      </span>
                      <span className="font-bold text-primary">
                        +${option.price}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mb-1">
                      {option.description}
                    </p>
                    <p className="text-xs text-accent-600">
                      Up to {option.maxItems} items
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Steps Navigation */}
            <div className="mb-8">
              <h3 className="font-headline text-lg font-semibold text-text-primary mb-4">
                Build Steps
              </h3>
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(index)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-organic ${
                      currentStep === index
                        ? 'bg-primary text-white' :'text-text-primary hover:bg-primary-50'
                    }`}
                  >
                    <Icon name={step.icon} size={20} />
                    <span className="font-medium">{step.name}</span>
                    {index < currentStep && (
                      <Icon name="Check" size={16} className="ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Items Summary */}
            <div className="mb-8">
              <h3 className="font-headline text-lg font-semibold text-text-primary mb-4">
                Your Plate ({selectedItems.length}/{maxItems})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedItems.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 bg-white rounded border"
                  >
                    <span className="text-sm font-medium text-text-primary truncate">
                      {item.name}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-primary">
                        ${item.price}
                      </span>
                      <button
                        onClick={() => handleItemSelect(item)}
                        className="text-error hover:text-error-600 transition-organic"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Nutritional Summary */}
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-text-primary mb-3">
                Nutritional Info
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-center p-2 bg-primary-50 rounded">
                  <div className="font-semibold text-primary">{totalNutrition.calories}</div>
                  <div className="text-text-secondary">Calories</div>
                </div>
                <div className="text-center p-2 bg-primary-50 rounded">
                  <div className="font-semibold text-primary">{totalNutrition.protein}g</div>
                  <div className="text-text-secondary">Protein</div>
                </div>
                <div className="text-center p-2 bg-primary-50 rounded">
                  <div className="font-semibold text-primary">{totalNutrition.carbs}g</div>
                  <div className="text-text-secondary">Carbs</div>
                </div>
                <div className="text-center p-2 bg-primary-50 rounded">
                  <div className="font-semibold text-primary">{totalNutrition.fat}g</div>
                  <div className="text-text-secondary">Fat</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Items Selection */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-6">
              <h3 className="font-headline text-xl font-semibold text-text-primary mb-2">
                {steps[currentStep].name}
              </h3>
              <p className="text-text-secondary">
                {currentStep === 0 && "Start with a base for your plate"}
                {currentStep === 1 && "Choose your main protein"}
                {currentStep === 2 && "Add delicious sides and salads"}
                {currentStep === 3 && "Finish with sauces and extras"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentStepItems.map(item => {
                const isSelected = selectedItems.find(selected => selected.id === item.id);
                const canSelect = !isSelected && selectedItems.length < maxItems;
                
                return (
                  <div
                    key={item.id}
                    className={`bg-white rounded-lg border-2 overflow-hidden transition-organic cursor-pointer ${
                      isSelected
                        ? 'border-primary shadow-warm'
                        : canSelect
                        ? 'border-primary-200 hover:border-primary-300' :'border-gray-200 opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => canSelect || isSelected ? handleItemSelect(item) : null}
                  >
                    {item.image && (
                      <div className="relative h-32">
                        <Image
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <div className="bg-primary text-white rounded-full p-2">
                              <Icon name="Check" size={20} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-text-primary text-sm">
                          {item.name}
                        </h4>
                        <span className="font-bold text-primary">
                          ${item.price}
                        </span>
                      </div>
                      
                      {item.description && (
                        <p className="text-text-secondary text-xs line-clamp-2">
                          {item.description}
                        </p>
                      )}
                      
                      {item.nutritional && (
                        <div className="flex items-center space-x-2 mt-2 text-xs text-text-secondary">
                          <span>{item.nutritional.calories} cal</span>
                          <span>•</span>
                          <span>{item.nutritional.protein}g protein</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-primary-100 bg-surface">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-primary-200 text-text-primary hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-organic"
            >
              <Icon name="ChevronLeft" size={16} />
              <span>Previous</span>
            </button>
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="flex items-center space-x-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </button>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={selectedItems.length === 0}
                className="btn-conversion disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Cart - ${totalPrice.toFixed(2)}
              </button>
            )}
          </div>
          
          <div className="text-right">
            <div className="text-sm text-text-secondary">Total Price</div>
            <div className="font-headline text-2xl font-bold text-primary">
              ${totalPrice.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildYourPlate;